import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Tabs, Table, Tag, Button, Input, Drawer, Descriptions,
  Statistic, Row, Col, notification, Space, Typography, Select,
} from 'antd';
import {
  MailOutlined, SendOutlined, HeartOutlined,
  PaperClipOutlined, ReloadOutlined, CheckCircleOutlined,
  CloseCircleOutlined, ApiOutlined, DownloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { mailApi, EmailListItem, EmailDetail, OutboxItem, HealthSummary } from '../api/mail';

const { Title } = Typography;

const resultColor: Record<string, string> = {
  imported: 'green',
  forwarded: 'blue',
  error: 'red',
};

const statusColor: Record<string, string> = {
  pending: 'default',
  sending: 'processing',
  sent: 'green',
  error: 'red',
};

const rateColor = (rate: number) => (rate > 90 ? '#52c41a' : rate > 70 ? '#faad14' : '#ff4d4f');

const MailPage: React.FC = () => {
  const navigate = useNavigate();
  // --- Inbox ---
  const [inbox, setInbox] = useState<EmailListItem[]>([]);
  const [inboxTotal, setInboxTotal] = useState(0);
  const [inboxPage, setInboxPage] = useState(1);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [senderFilter, setSenderFilter] = useState('');
  const [selectedEmail, setSelectedEmail] = useState<EmailDetail | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [checkLoading, setCheckLoading] = useState(false);

  // --- Outbox ---
  const [outbox, setOutbox] = useState<OutboxItem[]>([]);
  const [outboxTotal, setOutboxTotal] = useState(0);
  const [outboxPage, setOutboxPage] = useState(1);
  const [outboxLoading, setOutboxLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>();

  // --- Health ---
  const [health, setHealth] = useState<HealthSummary | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);

  const fetchInbox = useCallback(async (page = 1, sender = '') => {
    setInboxLoading(true);
    try {
      const { data } = await mailApi.getInbox({
        page, page_size: 50, sender: sender || undefined,
      });
      setInbox(data.items);
      setInboxTotal(data.total);
    } catch {
      notification.error({ message: 'Ошибка загрузки входящих' });
    }
    setInboxLoading(false);
  }, []);

  const fetchOutbox = useCallback(async (page = 1, status?: string) => {
    setOutboxLoading(true);
    try {
      const { data } = await mailApi.getOutbox({
        page, page_size: 50, status: status || undefined,
      });
      setOutbox(data.items);
      setOutboxTotal(data.total);
    } catch {
      notification.error({ message: 'Ошибка загрузки исходящих' });
    }
    setOutboxLoading(false);
  }, []);

  const fetchHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const { data } = await mailApi.getHealth();
      setHealth(data);
    } catch {
      notification.error({ message: 'Ошибка загрузки статуса здоровья' });
    }
    setHealthLoading(false);
  }, []);

  useEffect(() => { fetchInbox(); }, [fetchInbox]);

  const handleCheck = async () => {
    setCheckLoading(true);
    try {
      const { data } = await mailApi.triggerCheck();
      notification.success({
        message: 'Проверка почты',
        description: `Получено: ${data.fetched}, обработано: ${data.dispatched}`,
      });
      fetchInbox(inboxPage, senderFilter);
    } catch {
      notification.error({ message: 'Ошибка проверки почты' });
    }
    setCheckLoading(false);
  };

  const openDetail = async (id: number) => {
    try {
      const { data } = await mailApi.getInboxDetail(id);
      setSelectedEmail(data);
      setDrawerOpen(true);
    } catch {
      notification.error({ message: 'Ошибка загрузки письма' });
    }
  };

  const handleDownloadAttachment = async (id: number, filename: string | null) => {
    try {
      const response = await mailApi.downloadAttachment(id);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'attachment';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      notification.error({ message: 'Ошибка скачивания вложения' });
    }
  };

  // --- Columns ---
  const inboxColumns: ColumnsType<EmailListItem> = [
    {
      title: 'Дата', dataIndex: 'fetched_at', width: 150,
      render: (v: string) => dayjs(v).format('DD.MM.YY HH:mm'),
    },
    { title: 'Отправитель', dataIndex: 'sender', ellipsis: true },
    { title: 'Тема', dataIndex: 'subject', ellipsis: true },
    {
      title: '', dataIndex: 'has_attachment', width: 40, align: 'center',
      render: (v: boolean) => v ? <PaperClipOutlined /> : null,
    },
    {
      title: 'Обработка', dataIndex: 'processing_result', width: 120,
      render: (v: string | null) => v
        ? <Tag color={resultColor[v] || 'default'}>{v}</Tag>
        : '-',
    },
  ];

  const outboxColumns: ColumnsType<OutboxItem> = [
    {
      title: 'Дата', dataIndex: 'created_at', width: 150,
      render: (v: string) => dayjs(v).format('DD.MM.YY HH:mm'),
    },
    { title: 'Кому', dataIndex: 'to_email', ellipsis: true },
    { title: 'Тема', dataIndex: 'subject', ellipsis: true },
    { title: 'Модуль', dataIndex: 'module_name', width: 100 },
    {
      title: 'Статус', dataIndex: 'status', width: 100,
      render: (v: string) => <Tag color={statusColor[v] || 'default'}>{v}</Tag>,
    },
    { title: 'Попытки', dataIndex: 'attempts', width: 80, align: 'center' },
  ];

  const healthColumns: ColumnsType<any> = [
    {
      title: 'Тип', dataIndex: 'check_type', width: 120,
    },
    {
      title: 'Результат', dataIndex: 'success', width: 100,
      render: (v: boolean) => v
        ? <Tag icon={<CheckCircleOutlined />} color="success">OK</Tag>
        : <Tag icon={<CloseCircleOutlined />} color="error">Fail</Tag>,
    },
    {
      title: 'Время (мс)', dataIndex: 'duration_ms', width: 100, align: 'right',
    },
    {
      title: 'Когда', dataIndex: 'checked_at', width: 150,
      render: (v: string) => dayjs(v).format('DD.MM.YY HH:mm'),
    },
    {
      title: 'Ошибка', dataIndex: 'error_message', ellipsis: true,
    },
  ];

  return (
    <Card>
      <Space style={{ marginBottom: 8 }} align="center">
        <Title level={4} style={{ margin: 0 }}><MailOutlined /> Почта</Title>
        <Button icon={<ApiOutlined />} onClick={() => navigate('/mail/subscriptions')}>
          Подписки
        </Button>
      </Space>
      <Tabs
        defaultActiveKey="inbox"
        onChange={(key) => {
          if (key === 'inbox') fetchInbox();
          if (key === 'outbox') fetchOutbox();
          if (key === 'health') fetchHealth();
        }}
        items={[
          {
            key: 'inbox',
            label: <><MailOutlined /> Входящие</>,
            children: (
              <>
                <Space style={{ marginBottom: 16 }}>
                  <Input.Search
                    placeholder="Фильтр по отправителю"
                    allowClear
                    onSearch={(v) => { setSenderFilter(v); setInboxPage(1); fetchInbox(1, v); }}
                    style={{ width: 300 }}
                  />
                  <Button
                    icon={<ReloadOutlined />}
                    loading={checkLoading}
                    onClick={handleCheck}
                  >
                    Проверить почту
                  </Button>
                </Space>
                <Table
                  dataSource={inbox}
                  columns={inboxColumns}
                  rowKey="id"
                  loading={inboxLoading}
                  size="small"
                  onRow={(record) => ({
                    onClick: () => openDetail(record.id),
                    style: { cursor: 'pointer' },
                  })}
                  pagination={{
                    current: inboxPage,
                    total: inboxTotal,
                    pageSize: 50,
                    showSizeChanger: false,
                    onChange: (p) => { setInboxPage(p); fetchInbox(p, senderFilter); },
                  }}
                />
              </>
            ),
          },
          {
            key: 'outbox',
            label: <><SendOutlined /> Исходящие</>,
            children: (
              <>
                <Space style={{ marginBottom: 16 }}>
                  <Select
                    placeholder="Фильтр по статусу"
                    allowClear
                    style={{ width: 200 }}
                    onChange={(v) => { setStatusFilter(v); setOutboxPage(1); fetchOutbox(1, v); }}
                    options={[
                      { value: 'pending', label: 'Ожидает' },
                      { value: 'sending', label: 'Отправка' },
                      { value: 'sent', label: 'Отправлено' },
                      { value: 'error', label: 'Ошибка' },
                    ]}
                  />
                </Space>
                <Table
                  dataSource={outbox}
                  columns={outboxColumns}
                  rowKey="id"
                  loading={outboxLoading}
                  size="small"
                  pagination={{
                    current: outboxPage,
                    total: outboxTotal,
                    pageSize: 50,
                    showSizeChanger: false,
                    onChange: (p) => { setOutboxPage(p); fetchOutbox(p, statusFilter); },
                  }}
                />
              </>
            ),
          },
          {
            key: 'health',
            label: <><HeartOutlined /> Здоровье</>,
            children: health ? (
              <>
                <Row gutter={24} style={{ marginBottom: 24 }}>
                  <Col span={12}>
                    <Card>
                      <Statistic
                        title="IMAP (24ч)"
                        value={health.imap_success_rate_24h}
                        suffix="%"
                        valueStyle={{ color: rateColor(health.imap_success_rate_24h) }}
                      />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card>
                      <Statistic
                        title="SMTP (24ч)"
                        value={health.smtp_success_rate_24h}
                        suffix="%"
                        valueStyle={{ color: rateColor(health.smtp_success_rate_24h) }}
                      />
                    </Card>
                  </Col>
                </Row>
                <Descriptions bordered size="small" column={1} style={{ marginBottom: 24 }}>
                  <Descriptions.Item label="Последний IMAP OK">
                    {health.imap_last_success ? dayjs(health.imap_last_success).format('DD.MM.YY HH:mm') : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Последняя ошибка IMAP">
                    {health.imap_last_error || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Последний SMTP OK">
                    {health.smtp_last_success ? dayjs(health.smtp_last_success).format('DD.MM.YY HH:mm') : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Последняя ошибка SMTP">
                    {health.smtp_last_error || '-'}
                  </Descriptions.Item>
                </Descriptions>
                <Table
                  dataSource={health.recent}
                  columns={healthColumns}
                  rowKey={(_, i) => String(i)}
                  size="small"
                  pagination={false}
                />
              </>
            ) : (
              <div>Загрузка...</div>
            ),
          },
        ]}
      />
      <Drawer
        title={selectedEmail?.subject || 'Письмо'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={600}
      >
        {selectedEmail && (
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="От">{selectedEmail.sender}</Descriptions.Item>
            <Descriptions.Item label="Тема">{selectedEmail.subject}</Descriptions.Item>
            <Descriptions.Item label="Дата">
              {dayjs(selectedEmail.fetched_at).format('DD.MM.YYYY HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label="Вложение">
              {selectedEmail.has_attachment ? (
                <Space>
                  <PaperClipOutlined />
                  {selectedEmail.attachment_name}
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadAttachment(selectedEmail.id, selectedEmail.attachment_name)}
                  >
                    Скачать
                  </Button>
                </Space>
              ) : 'Нет'}
            </Descriptions.Item>
            <Descriptions.Item label="Обработано">
              {selectedEmail.processed_by || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Результат">
              {selectedEmail.processing_result
                ? <Tag color={resultColor[selectedEmail.processing_result]}>{selectedEmail.processing_result}</Tag>
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Подробности">
              {selectedEmail.processing_details || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Текст письма">
              <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto' }}>
                {selectedEmail.body}
              </pre>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </Card>
  );
};

export default MailPage;
